/**
 * Professional ART Form Modal
 * Cadastro e edição de uma ART do profissional (#345).
 *
 * Na edição a empresa fica desabilitada: o backend recusa a troca de
 * `companyId` (e de `professionalId`) com ValidationError, e o formulário não
 * deve oferecer o que a API recusa.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { AttachFile as AttachFileIcon } from '@mui/icons-material';

import { getApiErrorMessage } from '../../../types/common';
import type { ProfessionalArt, ProfessionalArtInput } from '../../../types/professionalArt';

export interface CompanyOption {
  id: number;
  name: string;
}

export interface ProfessionalArtFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Cria ou atualiza; o arquivo, quando houver, vai numa requisição própria. */
  onSubmit: (data: ProfessionalArtInput, file: File | null) => Promise<void>;
  /** Ausente = nova ART. */
  art?: ProfessionalArt | null;
  companies: CompanyOption[];
}

interface FormState {
  companyId: number | null;
  artNumber: string;
  issueDate: string;
  expiryDate: string;
  notes: string;
}

const ESTADO_INICIAL: FormState = {
  companyId: null,
  artNumber: '',
  issueDate: '',
  expiryDate: '',
  notes: '',
};

const ProfessionalArtFormModal: React.FC<ProfessionalArtFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  art,
  companies,
}) => {
  const isEditing = !!art;
  const [form, setForm] = useState<FormState>(ESTADO_INICIAL);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setFile(null);
    if (art) {
      setForm({
        companyId: art.companyId,
        artNumber: art.artNumber,
        issueDate: art.issueDate,
        expiryDate: art.expiryDate ?? '',
        notes: art.notes ?? '',
      });
    } else {
      setForm(ESTADO_INICIAL);
    }
  }, [open, art]);

  const selectedCompany = companies.find((company) => company.id === form.companyId) ?? null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFile(event.target.files?.[0] ?? null);
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (form.companyId === null) {
      setError('Empresa é obrigatória');
      return;
    }
    if (!form.artNumber.trim()) {
      setError('Número da ART é obrigatório');
      return;
    }
    if (!form.issueDate) {
      setError('Data de emissão é obrigatória');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSubmit(
        {
          companyId: form.companyId,
          artNumber: form.artNumber.trim(),
          issueDate: form.issueDate,
          expiryDate: form.expiryDate === '' ? null : form.expiryDate,
          notes: form.notes.trim() === '' ? null : form.notes.trim(),
        },
        file
      );
      onClose();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao salvar ART'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (): void => {
    if (!saving) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Editar ART' : 'Nova ART'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid size={12}>
              <Autocomplete
                options={companies}
                value={selectedCompany}
                disabled={isEditing}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_event, option) =>
                  setForm((prev) => ({ ...prev, companyId: option?.id ?? null }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empresa *"
                    helperText={
                      isEditing ? 'A empresa da ART não pode ser alterada.' : undefined
                    }
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                label="Número da ART *"
                value={form.artNumber}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, artNumber: event.target.value }))
                }
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Data de emissão *"
                value={form.issueDate}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, issueDate: event.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Validade"
                value={form.expiryDate}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, expiryDate: event.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                helperText="Em branco = ART sem validade"
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Observações"
                value={form.notes}
                onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </Grid>

            <Grid size={12}>
              <Button
                variant="outlined"
                startIcon={<AttachFileIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                Selecionar arquivo
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {file?.name ?? art?.originalName ?? 'Nenhum arquivo selecionado'}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            Salvar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProfessionalArtFormModal;
